import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RxHamburgerMenu } from "react-icons/rx";
import { ImCross } from "react-icons/im";
import LoginModal from "./LoginModal";
import { useSelector, useDispatch } from "react-redux";
import { toggleModal } from "../Redux/modalSlice";
import { FaChevronDown } from "react-icons/fa";
import { TokenManager } from "../apiConfig/apiinstance";
import apiInstance from "../apiConfig/apiinstance";
import { usePWA } from "../hooks/usePWA";

// const tags=[
//   "top",
//   "skirt",
//   "dress",
//   "bralette",
//   "pant"
// ]

export default function Header({ searchInput, setSearchInput }) {
  const navigate = useNavigate();

  const { canInstall, isInstalled, isStandalone, isIOS } = usePWA();
  const [searchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const currentCategory = searchParams.get("category");
  const [activeIndex, setActiveIndex] = useState(-1);
  const suggestionsRef = useRef([]);
  const searchContainerRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.modal.isModalOpen);
  const token = localStorage.getItem("token");
  const sidebarContentRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const [tags, setTags] = useState([]);

  // PWA installation states
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    const savedHistory = JSON.parse(
      localStorage.getItem("searchHistory") || "[]",
    );
    setSearchHistory(savedHistory);

    // Detect Safari for iPhone
    const userAgent = navigator.userAgent;
    const safari =
      /iPad|iPhone|iPod/.test(userAgent) &&
      /Safari/.test(userAgent) &&
      !/CriOS|FxiOS|OPiOS|mercury/i.test(userAgent);
    setIsSafari(safari);

    // Listen for PWA install prompt (Android)
    const handleBeforeInstallPrompt = (e) => {
      console.log("🚀 PWA install prompt available");
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      console.log("✅ PWA installed successfully");
      setDeferredPrompt(null);
      showAndroidSuccessNotification("PWA downloaded successfully!");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Android: Show success notification
  const showAndroidSuccessNotification = (message) => {
    const notification = document.createElement("div");
    notification.innerHTML = `
      <div style="
        display: flex; align-items: center; justify-content: center;
        padding: 16px 24px; font-size: 16px; font-weight: 600;
      ">
        <span style="margin-right: 12px; font-size: 24px;">✅</span>
        <span>${message}</span>
      </div>
    `;

    notification.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      background: #4CAF50; color: white; border-radius: 12px; z-index: 10001;
      min-width: 280px; max-width: 90%; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      animation: slideDown 0.3s ease-out;
    `;

    // Add animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideDown {
        from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
        to { transform: translateX(-50%) translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(notification);

    // Auto remove after 3 seconds
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.style.animation = "slideDown 0.3s ease-out reverse";
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
          if (document.head.contains(style)) {
            document.head.removeChild(style);
          }
        }, 300);
      }
    }, 3000);
  };

  // Enhanced PWA install handler
  const handleInstallPWAClick = async () => {
    setIsSidebarOpen(false);

    console.log("🔘 PWA Install clicked", {
      isIOS,
      isSafari,
      canInstall,
      hasDeferredPrompt: !!deferredPrompt,
    });

    // ANDROID: Direct PWA Installation
    if (!isIOS) {
      try {
        console.log("🤖 Starting Android PWA installation...");

        // Check if deferred prompt is available (required for proper installation)
        if (!deferredPrompt) {
          console.log("❌ No deferred prompt available - PWA not installable");

          // Show helpful message instead of false success
          const notification = document.createElement("div");
          notification.innerHTML = `
            <div style="
              display: flex; align-items: center; justify-content: center;
              padding: 16px 24px; font-size: 16px; font-weight: 600;
            ">
              <span style="margin-right: 12px; font-size: 24px;">ℹ️</span>
              <span>Please use browser menu "Add to Home Screen"</span>
            </div>
          `;

          notification.style.cssText = `
            position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
            background: #ff9800; color: white; border-radius: 12px; z-index: 10001;
            min-width: 280px; max-width: 90%; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            animation: slideDown 0.3s ease-out;
          `;

          document.body.appendChild(notification);
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 4000);

          return;
        }

        // Use deferred prompt for proper installation
        console.log("📲 Using deferred prompt for installation...");

        // Show the browser's native install dialog
        deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        console.log("👤 User choice result:", choiceResult);

        if (choiceResult.outcome === "accepted") {
          console.log("✅ User accepted installation");
          setDeferredPrompt(null);
          localStorage.setItem("pwa-installed", "true");
          showAndroidSuccessNotification("PWA downloaded successfully!");
        } else {
          console.log("❌ User dismissed installation");

          // Show cancellation message
          const notification = document.createElement("div");
          notification.innerHTML = `
            <div style="
              display: flex; align-items: center; justify-content: center;
              padding: 16px 24px; font-size: 16px; font-weight: 600;
            ">
              <span style="margin-right: 12px; font-size: 24px;">❌</span>
              <span>Installation cancelled</span>
            </div>
          `;

          notification.style.cssText = `
            position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
            background: #f44336; color: white; border-radius: 12px; z-index: 10001;
            min-width: 280px; max-width: 90%; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            animation: slideDown 0.3s ease-out;
          `;

          document.body.appendChild(notification);
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 3000);
        }
        return;
      } catch (error) {
        console.error("💥 Android installation error:", error);

        // Show error message instead of false success
        const notification = document.createElement("div");
        notification.innerHTML = `
          <div style="
            display: flex; align-items: center; justify-content: center;
            padding: 16px 24px; font-size: 16px; font-weight: 600;
          ">
            <span style="margin-right: 12px; font-size: 24px;">⚠️</span>
            <span>Installation failed. Try browser menu.</span>
          </div>
        `;

        notification.style.cssText = `
          position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
          background: #f44336; color: white; border-radius: 12px; z-index: 10001;
          min-width: 280px; max-width: 90%; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideDown 0.3s ease-out;
        `;

        document.body.appendChild(notification);
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 4000);

        return;
        showAndroidSuccessNotification("PWA downloaded successfully!");
      }
      return;
    }

    // IPHONE: Show Installation Instructions
    if (isIOS) {
      console.log("🍎 iPhone detected - showing installation instructions");

      // Show installation instructions alert
      alert(`📱 Install FP One on iPhone/iPad:

${
  isSafari
    ? "✅ Perfect! You're using Safari"
    : "⚠️ For best experience, open this page in Safari"
}

Quick Steps:
1️⃣ Tap the Share button (⎘) ${isSafari ? "at the bottom" : "in Safari"}
2️⃣ Scroll down and tap "Add to Home Screen"
3️⃣ Tap "Add" to install

✨ The app will appear on your home screen and work like a native app!

${
  !isSafari
    ? "\n💡 Tip: Copy this link and open it in Safari for the best installation experience."
    : ""
}`);

      // Mark as installed for UI purposes
      localStorage.setItem("pwa-installed", "true");
      return;
    }
  };

  // Hide search results/history on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setSearchResults([]);
        setShowHistory(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const saveToHistory = (query) => {
    if (!query.trim()) return;
    const updatedHistory = [
      query,
      ...searchHistory.filter((item) => item !== query),
    ].slice(0, 5);
    setSearchHistory(updatedHistory);
    console.log("Updated search history:", updatedHistory);
    localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
  };

  const checkScrollable = () => {
    if (sidebarContentRef.current) {
      const { scrollHeight, clientHeight, scrollTop } =
        sidebarContentRef.current;
      const isScrollable = scrollHeight > clientHeight;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
      setShowScrollIndicator(isScrollable && !isAtBottom);
    }
  };

  const handleScrollIndicatorClick = () => {
    if (sidebarContentRef.current) {
      sidebarContentRef.current.scrollBy({
        top: 200,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (isSidebarOpen) {
      const timer = setTimeout(checkScrollable, 100);
      return () => clearTimeout(timer);
    }
  }, [isSidebarOpen, categories]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    if (!isSidebarOpen) {
      setShowScrollIndicator(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiInstance.get(`/api/cat/v1/parent-categories/`);
      if (data.status === 200) {
        setCategories(data.data || []);
      } else {
        setError("No categories found");
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Error fetching categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const searchStyles = async (inputValue) => {
    if (!inputValue) {
      setSearchResults([]);
      return;
    }
    try {
      const { data } = await apiInstance.get(
        `/api/style/v1/stylekuchbhi/elasticsearch/?q=${inputValue}`,
      );
      if (
        data.status === 200 &&
        data?.data?.results &&
        Array.isArray(data.data.results)
      ) {
        const results = data.data.results.filter(
          (item) => item && item.style_id && item.name,
        );
        const options = results
          .map((item) => ({
            value: item.style_id,
            label: item.name,
          }))
          .slice(0, 5);

        if (options.length > 0) {
          // Results found, show "See all results" option + actual results
          options.unshift({
            value: "see-all",
            label: `See all results for "${inputValue}"`,
            isSearchAll: true,
          });
          setSearchResults(options);
        } else {
          // No valid results found, show "No results found" option
          setSearchResults([
            {
              value: "no-results",
              label: `No results found for "${inputValue}"`,
              isNoResults: true,
            },
          ]);
        }
      } else {
        // API error or no data, show "No results found" option
        setSearchResults([
          {
            value: "no-results",
            label: `No results found for "${inputValue}"`,
            isNoResults: true,
          },
        ]);
      }
    } catch (error) {
      // Error occurred, show "No results found" option
      setSearchResults([
        {
          value: "no-results",
          label: `No results found for "${inputValue}"`,
          isNoResults: true,
        },
      ]);
    }
  };

  const debouncedSearchInput = useDebounce(searchInput, 300);
  useEffect(() => {
    if (debouncedSearchInput.trim() === "") {
      setSearchResults([]);
      return;
    }
    setShowHistory(false);
    searchStyles(debouncedSearchInput);
  }, [debouncedSearchInput]);

  const handleInputChange = (inputValue) => {
    setSearchInput(inputValue);
    setActiveIndex(-1);
    if (inputValue.trim() === "") {
      setShowHistory(true);
      setSearchResults([]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        searchResults.length ? (prev + 1) % searchResults.length : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev <= 0 ? searchResults.length - 1 : prev - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && searchResults[activeIndex]) {
        handleSelect(searchResults[activeIndex]);
      } else if (searchInput.trim() !== "") {
        saveToHistory(searchInput);
        navigate(
          `/search-results?name=${encodeURIComponent(searchInput.trim())}`,
        );
        setSearchResults([]);
        setShowHistory(false);
      }
    } else if (e.key === "Escape") {
      setSearchResults([]);
      setShowHistory(false);
    }
  };

  useEffect(() => {
    if (activeIndex !== -1 && suggestionsRef.current[activeIndex]) {
      suggestionsRef.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const { data } = await apiInstance.get(
          "/api/style/v1/tags/list/top-5/",
        );
        if (data.status === 200 && Array.isArray(data.data)) {
          setTags(data.data.map((tag) => tag.name));
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    fetchTags();
  }, []);

  const handleSelect = (selected) => {
    saveToHistory(selected.label);
    console.log("Selected search term:", selected);
    if (selected.value === "see-all" || selected.value === "no-results") {
      const searchTerm = selected.label.match(/"([^"]+)"/)?.[1] || searchInput;
      navigate(`/search-results?name=${encodeURIComponent(searchTerm)}`);
    } else {
      navigate(`/search/product-detail/${selected.value}`, { replace: true });
    }
    setSearchResults([]);
    setShowHistory(false);
  };

  const handleCategoryClick = (name) => {
    navigate(`/filter-product/${name}/?view=apparel`);
    setIsSidebarOpen(false);
  };

  const handleLogout = () => {
    TokenManager.clearTokens();
    navigate("/");
    setSearchResults([]);
    setSearchInput("");
    localStorage.removeItem("path");
    setSearchInput("");
    setSearchResults([]);
    setIsSidebarOpen(false);
    window.dispatchEvent(new Event("tokenUpdated"));
    window.location.reload();
    navigate("/", { state: { backgroundLocation: { pathname: "/" } } });
  };

  const handleHistoryClick = (query) => {
    setSearchInput(query);
    saveToHistory(query);
    navigate(`/search-results?name=${encodeURIComponent(query)}`, {
      replace: true,
    });
    setShowHistory(false);
  };

  const removeFromHistory = (indexToRemove) => {
    const updatedHistory = searchHistory.filter(
      (_, index) => index !== indexToRemove,
    );
    setSearchHistory(updatedHistory);
    localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem("searchHistory");
  };

  return (
    <header className="header fixed top-0 z-[100]">
      <h2
        onClick={() => navigate("/")}
        className="cursor-pointer hidden md:block"
      >
        FP One
      </h2>

      <div className="header-search">
        <RxHamburgerMenu className="hamburger-icon" onClick={toggleSidebar} />
        {localStorage.getItem("token") && (
          <div className="input-header relative" ref={searchContainerRef}>
            <input
              type="text"
              placeholder="Search"
              value={searchInput}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (searchInput.trim()) {
                  searchStyles(searchInput);
                  setShowHistory(false);
                } else {
                  setShowHistory(true);
                  setSearchResults([]);
                }
              }}
              className="search-input px-4 py-2 border border-gray-300 rounded w-full"
            />

            {(searchResults.length > 0 || showHistory) && (
              <ul className="absolute bg-white border w-[80%] md:w-[100%] mt-1 z-10 shadow-lg rounded-md max-h-[250px] overflow-y-auto">
                {showHistory &&
                  tags.map((item, idx) => {
                    return (
                      <li
                        key={idx}
                        onClick={() => {
                          setShowHistory(false);
                          return navigate(`/tag/${item}?view=apparel`);
                        }}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-200 w-full"
                      >
                        # {item}
                      </li>
                    );
                  })}

                {showHistory && (
                  <>
                    <li
                      className="px-4 py-2 cursor-pointer hover:bg-gray-200 w-full border-t"
                      onClick={() => {
                        setShowHistory(false);
                        return navigate("all-tag");
                      }}
                    >
                      Show All Tags{" "}
                    </li>
                  </>
                )}

                {/* {showHistory && searchHistory.length > 0 && (
                  <>
                    <li className="px-4 py-2 text-gray-500 text-sm font-medium border-b">
                      Recent Searches
                    </li>
                    {searchHistory.map((item, index) => (
                      <div
                        className="flex justify-between items-center"
                        key={index}
                      >
                        <li
                          className="px-4 py-2 cursor-pointer hover:bg-gray-200 w-full"
                          onClick={() => handleHistoryClick(item)}
                        >
                          {item}
                        </li>
                        <button
                          className="py-[12px] px-[16px] w-2 h-2 flex items-center justify-center !bg-white text-[25px] hover:scale-125 text-red-500 cursor-pointer rounded-none"
                          onClick={() => removeFromHistory(index)}
                        >
                          X
                        </button>
                      </div>
                    ))}
                    <li
                      className="px-4 py-2 text-red-500 cursor-pointer hover:bg-gray-200"
                      onClick={clearHistory}
                    >
                      Clear History
                    </li>
                  </>
                )} */}
                {!showHistory &&
                  searchResults.map((item, index) => (
                    <li
                      key={item.value}
                      ref={(el) => (suggestionsRef.current[index] = el)}
                      onClick={() => handleSelect(item)}
                      className={`px-4 py-2 cursor-pointer ${
                        activeIndex === index
                          ? "bg-blue-500 text-white"
                          : "hover:bg-gray-100"
                      } ${item.isSearchAll ? "font-semibold" : ""}`}
                    >
                      {item.label}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {token ? (
        <h2 className="login">
          <a onClick={handleLogout}>Logout</a>
        </h2>
      ) : (
        <h2 className="login">
          <a onClick={() => dispatch(toggleModal())}>Login</a>
        </h2>
      )}

      <LoginModal show={isOpen} onHide={() => dispatch(toggleModal())} />
      {isSidebarOpen && (
        <div>
          <div className="sidebar-overlay" onClick={toggleSidebar}></div>

          <div className="sidebar-left mb-4">
            <div className="icon-cross sticky top-0 bg-white">
              <h2
                onClick={() => {
                  navigate("/");
                  setIsSidebarOpen(false);
                }}
                className="select-none cursor-pointer"
              >
                FP One
              </h2>
              <ImCross className="sidebar-close-icon" onClick={toggleSidebar} />
            </div>

            <div
              className="sidebar-content"
              ref={sidebarContentRef}
              onScroll={checkScrollable}
            >
              {token && (
                <div className="sidebar-nav-section">
                  <ul>
                    {categories.map((item) => (
                      <li key={item.id}>
                        <p
                          className={
                            currentCategory === item.slug ? "active" : ""
                          }
                          onClick={() => handleCategoryClick(item.slug)}
                        >
                          {item.name}
                        </p>
                      </li>
                    ))}

                    <hr className="mt-10 mb-8" />

                    {["print", "embroidery", "lace", "pintuck"].map((item) => (
                      <li key={item}>
                        <a
                          className="cursor-pointer"
                          onClick={() => {
                            if (!loading) {
                              navigate(`/tag/${item}?view=apparel`);
                              setIsSidebarOpen(false);
                            }
                          }}
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="sidebar-nav-section sidebar-special-section">
                <ul>
                  {token && (
                    <li>
                      <a
                        onClick={() => {
                          navigate("/wishlist");
                          setIsSidebarOpen(false);
                        }}
                      >
                        Go To Likes
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div
              className={`scroll-indicator ${
                showScrollIndicator ? "visible" : ""
              }`}
              onClick={handleScrollIndicatorClick}
            >
              <FaChevronDown />
            </div>

            <ul>
              <li>
                {!isInstalled ? (
                  <p
                    className="button"
                    onClick={handleInstallPWAClick}
                    style={{ cursor: "pointer" }}
                  >
                    Install App
                  </p>
                ) : (
                  <>
                    {!isStandalone && (
                      <p className="button" style={{ opacity: 0.6 }}>
                        App Installed
                      </p>
                    )}
                  </>
                )}
              </li>
              {token ? (
                <li className="button">
                  <p onClick={handleLogout}>Logout</p>
                </li>
              ) : (
                <li className="button">
                  <p
                    onClick={() => {
                      dispatch(toggleModal());
                      setIsSidebarOpen(false);
                    }}
                  >
                    Login
                  </p>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
}

function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
